import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * Where an OPEN dropdown menu goes, in viewport space.
 *
 * Extracted from PaginatedSelect (6e1) when the Fan rated table grew a second
 * custom dropdown (FanWideHighSelect) with the same two problems — a scrolling
 * host that would clip and reflow around an absolutely positioned menu, and a
 * CSS `zoom` on the board that re-scales whatever lengths we hand back. Both
 * selects share one implementation so a fix to either lands on both.
 */

/** `.custom-select-with-pagination ul.options { max-height: 200px }`
 * (common.css:32603) — mirrored here only to decide which side to open on. */
export const MENU_MAX_HEIGHT = 200;

/** Anchor for the open menu, in the portal's own (zoomed) px. `off` = the
 * control has scrolled out of its scrolling host, so the menu hides with it. */
export interface MenuPos {
  top: number;
  left: number;
  width: number;
  off: boolean;
}

/** Nearest scrolling ancestor — the Data Bar panel that used to clip the menu
 * (`#forms_sections`, `#forms_ahu`), or the rated table's own scroll box
 * (`.fan_rate-list-inline`), found by computed style rather than by id so a
 * panel that gains a scrollbar later is covered too. */
export const scrollParent = (el: HTMLElement): HTMLElement | null => {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === 'auto' || oy === 'scroll') return n;
  }
  return null;
};

/** Cumulative CSS `zoom` in effect on `el` — `currentCSSZoom` where the browser
 * has it, otherwise the product of the cascade's own `zoom` values. */
export const cssZoom = (el: HTMLElement): number => {
  const native = (el as HTMLElement & { currentCSSZoom?: number }).currentCSSZoom;
  if (typeof native === 'number' && native > 0) return native;
  let z = 1;
  for (let n: HTMLElement | null = el; n; n = n.parentElement) {
    const v = parseFloat(getComputedStyle(n).zoom);
    if (v && v !== 1) z *= v;
  }
  return z;
};

/**
 * The open dropdown is anchored in VIEWPORT space, not inside its host panel.
 *
 * common.css gives `ul.options` `position: absolute` under the control, which
 * puts it inside whichever panel hosts the select — and those are scroll
 * containers (`#forms_sections` is `overflow-y: auto; overflow-x: hidden`,
 * ahu/styles.css:1749; `#forms_ahu` likewise, :1636; the fan rated table is
 * `overflow: auto`). An absolutely positioned box still counts towards its
 * scroller's scrollable overflow, so a 200px menu opened on a field in the
 * bottom ~216px of the panel grew the Data Bar's scroll area for exactly as long
 * as the menu was open: the 10px scrollbar appeared, every row reflowed 10px
 * narrower, and the part of the menu below the panel edge was clipped away by
 * the same `overflow` (so the options you had to scroll for were the ones you
 * could not see).
 *
 * `position: fixed` takes it out of the scroller's overflow AND out of its clip
 * — a fixed box is only clipped by an ancestor that is its containing block,
 * which plain `overflow` does not make one — so the panel's geometry no longer
 * changes at all when a select opens.
 *
 * The division by `cssZoom()` is the board's own doing: `body` carries a CSS
 * `zoom` (~0.76, the scale-to-fit on /ahu/detail). `getBoundingClientRect()`
 * reports post-zoom viewport px while the lengths we hand back are re-scaled by
 * that same zoom when painted, so they have to be divided out first. It is read
 * off the cascade rather than inferred from `rect.height ÷ offsetHeight` because
 * `offsetHeight` is rounded to whole px: on an 18px-tall control that rounding
 * is 1.3% of the factor, which lands the menu ~7px above the field.
 *
 * `deps` is whatever makes the menu taller (its option count), since that can
 * change the side it belongs on.
 */
export const useFixedMenuPosition = (
  open: boolean,
  rootRef: RefObject<HTMLElement | null>,
  listRef: RefObject<HTMLElement | null>,
  deps: unknown[] = []
): MenuPos | null => {
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return undefined;
    }
    const place = (e?: Event) => {
      const el = rootRef.current;
      // The list is itself a 200px scroller, and a capture-phase listener on
      // window sees its scrolls too. Paging through a long catalogue must not
      // re-measure (and re-render) the menu on every wheel tick — the menu has
      // not moved.
      if (e && listRef.current && e.target === listRef.current) return;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Measured where the menu LIVES (the portal under <body>), not where the
      // control does — that is the zoom its own lengths get re-scaled by.
      const z = cssZoom(listRef.current ?? el);
      // Painted height of the menu — the real one once it has rendered, the
      // stylesheet's max-height as the estimate for the very first pass.
      const menuH = (listRef.current?.offsetHeight || MENU_MAX_HEIGHT) * z;
      // Flip above the control only when below genuinely cannot hold it and
      // above can: the data bar runs to the bottom of the window, so the last
      // few fields have no room under them.
      const below = window.innerHeight - r.bottom;
      const flip = below < menuH && r.top > below;
      // Being out of the panel's clip is right while the field is on screen and
      // wrong once it is not: scrolling the Data Bar used to carry the menu away
      // with its field, over the tab strip and the header, because nothing
      // bounds a fixed box. Legacy clipped the two together. Hide the menu when
      // its control has left the panel entirely — partial overlap still shows,
      // which is the whole point of escaping the clip.
      // (Both axes: the fan rated table scrolls sideways too, so a control can
      // leave its host out of the left or right edge just as well.)
      const panel = scrollParent(el);
      const pr = panel?.getBoundingClientRect();
      const offPanel =
        !!pr &&
        (r.bottom <= pr.top || r.top >= pr.bottom || r.right <= pr.left || r.left >= pr.right);
      const top = (flip ? r.top - menuH : r.bottom) / z;
      const left = r.left / z;
      const width = r.width / z;
      // Keep the previous object when nothing moved: `place` runs on every
      // scroll event in the document, and a fresh object would re-render the
      // whole option list each time.
      setMenuPos((prev) =>
        prev && prev.top === top && prev.left === left && prev.width === width && prev.off === offPanel
          ? prev
          : { top, left, width, off: offPanel }
      );
    };
    place();
    // Capture phase: the data bar scrolls, not the window, so the scroll event
    // never reaches window in the bubble phase.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ...deps]);
  return menuPos;
};
